/**
 * 🆕 [Phase 50] Bloc enrichi décrivant la section « Avertissements ».
 *
 * Source unique de vérité — utilisé sur :
 *   - /mon-bonus → MonBonus.jsx (dialog "Comprendre ce critère")
 *   - /gestion-avertissements → GestionAvertissements.jsx (dialog "ℹ️ Comprendre")
 *
 * Si tu changes le texte ici, il est mis à jour partout. Ne crée pas de copie.
 */

export const AvertissementsRichBlock = () => {
  const TYPES = [
    ["Géolocalisation hors zone", "L'employé a effectué son punch à l'extérieur de la zone prévue du chantier ou du lieu de travail assigné."],
    ["Absence non justifiée", "L'employé ne s'est pas présenté au travail sans avoir fourni une justification valide ou sans avoir avisé son responsable selon les procédures établies."],
    ["Mesures sécurité non conformes", "L'employé n'a pas respecté les consignes de sécurité applicables sur le chantier ou a adopté un comportement pouvant compromettre sa sécurité ou celle des autres."],
    ["Cigarette", "L'employé ne doit pas fumer sur le terrain des clients ni jeter de mégots ou de déchets liés à la cigarette sur leur propriété. Ceux-ci doivent être disposés proprement, dans un endroit approprié et hors de la vue des clients."],
    ["Pause non respectée", "L'employé n'a pas respecté la durée, l'horaire ou les règles prévues concernant les pauses."],
    ["Oubli de punch", "L'employé a oublié de poinçonner son entrée, sa sortie ou une pause, ce qui empêche un suivi exact de ses heures travaillées."],
    ["Port EPI non conforme", "L'employé ne portait pas les équipements de protection individuelle requis pour le travail à effectuer, comme les lunettes, gants, masque, casque ou autres équipements obligatoires."],
    ["Vêtements non conformes", "L'employé ne portait pas une tenue de travail appropriée ou conforme aux standards de l'entreprise et/ou aux exigences du chantier."],
    ["Retard", "L'employé est arrivé en retard au chantier, au bureau ou à tout autre lieu de travail prévu, sans justification valable ou sans avis préalable."],
    ["Attitude", "L'employé a eu un comportement, une attitude ou une façon de communiquer qui ne respecte pas les standards professionnels attendus envers les collègues, clients, fournisseurs ou responsables."],
    ["Autre", "Avertissement utilisé pour une situation particulière qui ne correspond pas aux autres catégories. Une description détaillée doit être ajoutée pour expliquer la situation."],
  ];
  return (
    <div className="text-sm text-slate-700 leading-relaxed break-words space-y-4" data-testid="avertissements-rich-block">
      <p>
        Cette section regroupe les avertissements officiels qui peuvent influencer l&apos;admissibilité au bonus.
      </p>
      <p>
        Chaque type d&apos;avertissement a une limite permise pour la saison. Tant que la limite n&apos;est pas dépassée, le critère reste conforme.
      </p>
      <p>
        Par exemple, <strong>Retard&nbsp;0/3</strong> signifie que tu as actuellement <strong>0 retard comptabilisé</strong> sur une limite de <strong>3 retards permis</strong> pour la saison.
      </p>
      <p>
        L&apos;objectif n&apos;est pas de pénaliser une petite erreur isolée, mais de s&apos;assurer que le bonus récompense les employés fiables, professionnels, sécuritaires et respectueux des standards de l&apos;entreprise.
      </p>
      <p>
        Seuls les avertissements validés officiellement par un gestionnaire comptent dans ce calcul.
      </p>

      <hr className="border-slate-200" />

      <h3 className="text-base font-semibold text-slate-900">Types d&apos;avertissements</h3>
      <dl className="space-y-3">
        {TYPES.map(([titre, desc]) => (
          <div key={titre}>
            <dt className="font-semibold text-slate-900">{titre}</dt>
            <dd className="text-slate-700 mt-0.5">{desc}</dd>
          </div>
        ))}
      </dl>

      <hr className="border-slate-200" />

      <h3 className="text-base font-semibold text-slate-900">Important</h3>
      <p>
        Un avertissement n&apos;est pas automatiquement appliqué dès qu&apos;une situation survient. Il doit d&apos;abord être validé officiellement par un gestionnaire.
      </p>
      <p>
        Le but de cette section est d&apos;assurer une gestion juste, transparente et uniforme pour tous les employés pendant la saison.
      </p>
    </div>
  );
};

export default AvertissementsRichBlock;
